import {clsx} from 'clsx'
import {useEffect, useRef, useState} from 'react'
import {useMarkersActions} from '@github-ui/pull-requests/pull-request-store-selectors'
import {ReviewThreadHeader} from './ReviewThreadHeader'
import {CommentContentSkeleton} from './CommentContentSkeleton'
import type {LineKey} from '../../types'
import {fileLevelCommentKey} from '../../types'
import type {Thread} from '../../conversation-types'
import {useReviewThreadCollapseState} from '@github-ui/use-review-thread-collapse-state'

interface ReviewThreadWithoutDataProps {
  thread: Thread
  isFirstMarker: boolean
  isLastThread: boolean
  isInDialogMode: boolean
}

export function ReviewThreadWithoutData({
  thread,
  isFirstMarker,
  isLastThread,
  isInDialogMode = false,
}: ReviewThreadWithoutDataProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isInView, setIsInView] = useState(false)
  const {updateThread} = useMarkersActions()
  // Initialize collapsed state from localStorage, defaulting to false
  const [isCollapsed, setIsCollapsed] = useReviewThreadCollapseState(thread.id, false)

  // Detect when element enters/exits viewport
  useEffect(() => {
    const element = containerRef.current
    if (!element) return

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]) {
          setIsInView(entries[0].isIntersecting)
        }
      },
      {
        threshold: 0.75, // Consider in view if 75% of the component is visible
      },
    )

    observer.observe(element)
    return () => observer.disconnect()
  }, [thread.id])

  // When thread enters viewport and has full data loaded, transition to full display
  useEffect(() => {
    if (!isInView || !thread.positioning) return

    // If we have the full thread data (with commentsData), update it to display in diff
    if (thread?.commentsData) {
      // Update the thread to allow it to render in diff lines
      if (thread.hideUntilLoadedAndInView) {
        const threadId = Number(thread.id)

        const filePath = thread.positioning.path
        const isRightOrLeftSide = thread.positioning.endDiffSide === 'RIGHT' ? 'R' : 'L'
        const lineKey =
          thread.positioning.endLine !== null && thread.positioning.endLine !== undefined
            ? (`${isRightOrLeftSide}${thread.positioning.endLine}` as LineKey)
            : fileLevelCommentKey

        updateThread(threadId, filePath, lineKey, old => {
          if (!old) return thread
          return {...old, hideUntilLoadedAndInView: false, shouldRenderInDiffLines: true}
        })
      }
    }
  }, [isInView, updateThread, thread])

  const handleToggleCollapsed = () => {
    setIsCollapsed((prevIsCollapsed: boolean) => !prevIsCollapsed)
  }

  return (
    <div
      ref={containerRef}
      key={`review-thread-without-data-${thread.id}`}
      className={clsx(
        'mt-1 border rounded-2 color-border-default color-shadow-small bgColor-default',
        isLastThread ? 'mb-1' : 'mb-2',
      )}
      data-testid={`review-thread-without-data-${thread.id}`}
      data-first-marker={isFirstMarker}
      data-marker-id={`${thread.id}`}
      data-is-in-view={isInView}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={isInDialogMode ? 0 : -1}
    >
      <ReviewThreadHeader
        thread={thread}
        isCollapsed={isCollapsed}
        isFirstThread={isFirstMarker}
        onToggleCollapsed={handleToggleCollapsed}
        showLabels={false}
      />
      {!isCollapsed && <CommentContentSkeleton />}
    </div>
  )
}
